// sales-files-v1
import { useCallback, useEffect, useRef, useState } from "react";
import { Download, FileText, Loader2, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { getErrorMessage } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

import { confirmAction } from "@/lib/dialogs";
export const ACCEPT = ".pdf,.jpg,.jpeg,.png,.dwg,.dxf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip";
export const fmtSize = (n) => (n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
const save = (blob, name) => { const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = name; a.click(); setTimeout(() => URL.revokeObjectURL(url), 2000); };

function Section({ title, hint, kind, files, busy, onUpload, onDownload, onDelete }) {
  const ref = useRef(null);
  return (
    <div className="rounded-xl border border-border p-3" data-testid={`files-${kind}`}>
      <div className="flex items-start justify-between gap-3">
        <div><div className="text-sm font-semibold">{title}</div><div className="text-xs text-muted-foreground">{hint}</div></div>
        <>
          <input ref={ref} type="file" accept={ACCEPT} multiple className="hidden" data-testid={`upload-${kind}`} onChange={(e) => { onUpload(kind, [...e.target.files]); e.target.value = ""; }} />
          <Button size="sm" variant="outline" disabled={busy} onClick={() => ref.current?.click()}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload</Button>
        </>
      </div>
      <ul className="mt-2 divide-y divide-border">
        {files.map((f) => (
          <li key={f.id} className="flex items-center gap-3 py-2" data-testid="proposal-file">
            <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
            <div className="min-w-0 flex-1"><div className="truncate text-sm">{f.filename}</div><div className="text-[11px] text-muted-foreground">{fmtSize(f.size_bytes)}{f.uploaded_by_name ? ` · ${f.uploaded_by_name}` : ""}</div></div>
            <Button size="icon" variant="ghost" className="h-8 w-8" title="Download" onClick={() => onDownload(f)}><Download className="h-4 w-4" /></Button>
            <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" title="Delete" onClick={() => onDelete(f)}><Trash2 className="h-4 w-4" /></Button>
          </li>
        ))}
        {files.length === 0 && <li className="py-2 text-xs text-muted-foreground">Nothing uploaded yet.</li>}
      </ul>
    </div>
  );
}

/** Design drawings and the client PO document of a proposal. */
export function ProposalFilesDialog({ row, onClose, onDone }) {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState("");
  const load = useCallback(async () => {
    try { const { data } = await api.get(`/pipeline/proposals/${row.id}/files`); setFiles(data); } catch (ex) { toast.error(getErrorMessage(ex)); }
  }, [row.id]);
  useEffect(() => { load(); }, [load]);
  const upload = async (kind, list) => {
    setBusy(kind);
    for (const file of list) {
      const fd = new FormData(); fd.append("file", file); fd.append("kind", kind);
      try { await api.post(`/pipeline/proposals/${row.id}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success(`${file.name} uploaded`); }
      catch (ex) { toast.error(`${file.name}: ${getErrorMessage(ex)}`, { duration: 9000 }); }
    }
    setBusy(""); await load(); onDone && onDone();
  };
  const download = async (f) => {
    try { const { data } = await api.get(`/pipeline/proposals/${row.id}/files/${f.id}/download`, { responseType: "blob" }); save(data, f.filename); } catch (ex) { toast.error(getErrorMessage(ex)); }
  };
  const remove = async (f) => {
    if (!await confirmAction(`Delete ${f.filename}?`)) return;
    try { await api.delete(`/pipeline/proposals/${row.id}/files/${f.id}`); await load(); onDone && onDone(); } catch (ex) { toast.error(getErrorMessage(ex)); }
  };
  const sec = (kind) => files.filter((f) => f.kind === kind);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-xl" data-testid="files-dialog">
        <DialogHeader><DialogTitle>Files · {row.code}</DialogTitle></DialogHeader>
        <Section kind="DESIGN" title="Design drawings" hint="Plans, elevations, 3D views. These are emailed with the proposal." files={sec("DESIGN")} busy={busy === "DESIGN"} onUpload={upload} onDownload={download} onDelete={remove} />
        <Section kind="PO" title="Client PO document" hint="The purchase order / work order received from the client." files={sec("PO")} busy={busy === "PO"} onUpload={upload} onDownload={download} onDelete={remove} />
        <p className="text-xs text-muted-foreground">PDF, images, DWG/DXF, Word, Excel, PowerPoint or ZIP. Up to 50 MB each.</p>
      </DialogContent>
    </Dialog>
  );
}
